import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import useIsIntegrated from "../../../api/hooks/useIsIntegrated";
import {
  integrationsInfoKeys,
  platformUsersKeys,
  userAccessControlKeys,
} from "../../../api/queryKeys";
import FilterMenu from "../../../components/filters/FilterMenu";
import SearchInput from "../../../components/filters/SearchInput";
import InitialScanBanner from "../../../components/InitialScanBanner";
import AppPage from "../../../components/structure/AppPage";
import { PLATFORM_USER_ACTIONS } from "../../../utils/constants";
import { logos } from "../environment/icons/iconExport";
import IntegrationCard from "./components/IntegrationCard";
import { IntegrationsMapping } from "./IntegrationsMapping";

const IntegrationTabs = {
  Available: "Available",
  Connected: "Connected",
  Unconnected: "Unconnected",
  Upcoming: "Upcoming",
};

const Integrations = () => {
  const colors = useColors();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const integrations = Object.keys(IntegrationsMapping)
    .filter((integration) => integration !== "breeze")
    .sort((a, b) => {
      return IntegrationsMapping[a].displayName.localeCompare(
        IntegrationsMapping[b].displayName
      );
    });
  const [integrated, setIntegrated] = useState({});

  const [activeTab, setActiveTab] = useState(IntegrationTabs.Available);
  const toolFromLink = location.state?.filterIntegration;
  const toolNameFromLink = location.state?.toolName;
  const { integrations: integratedList, isIntegratedLoading } =
    useIsIntegrated();
  const [integrationPermissions, setIntegrationPermissions] = useState({});
  const { getActionToRoleMapping, getPlatformUser, getIntegrationsInfo } =
    useAPI();
  const [filteredIntegrationsCards, setFilteredIntegrationsCards] =
    useState(integrations);

  const groupedIntegrations = useMemo(
    () => ({
      [IntegrationTabs.Connected]: filteredIntegrationsCards.filter(
        (integration) =>
          IntegrationsMapping[integration]["available"] &&
          integrated[integration]
      ),
      [IntegrationTabs.Unconnected]: filteredIntegrationsCards.filter(
        (integration) =>
          IntegrationsMapping[integration]["available"] &&
          !integrated[integration]
      ),
      [IntegrationTabs.Available]: filteredIntegrationsCards.filter(
        (integration) => IntegrationsMapping[integration]["available"]
      ),
      [IntegrationTabs.Upcoming]: filteredIntegrationsCards.filter(
        (integration) => !IntegrationsMapping[integration]["available"]
      ),
    }),
    [filteredIntegrationsCards, integrated]
  );

  const integrationsTypesDefault = Object.values(IntegrationsMapping)
    .map((integration) => integration.category)
    .filter((value) => value != null)
    .filter((value, index, self) => self.indexOf(value) === index)
    .sort();

  const [filterOptions, setFilterOptions] = useState({
    integrationTypes: ["All", ...integrationsTypesDefault],
    IntegrationTabs: Object.keys(IntegrationTabs),
    searchString: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    integrationTypes: {},
    integrationStatus: IntegrationTabs.Available,
    searchString: "",
  });

  useEffect(() => {
    let filteredIntegrations = Object.keys(IntegrationsMapping)
      .filter((integration) => integration !== "breeze")
      .sort((a, b) => {
        return IntegrationsMapping[a].displayName.localeCompare(
          IntegrationsMapping[b].displayName
        );
      });

    if (Object.keys(appliedFilters.integrationTypes)?.length > 0) {
      const selectedIntegrationTypes = Object.keys(
        appliedFilters.integrationTypes
      ).filter((key) => appliedFilters.integrationTypes[key] === true);

      if (selectedIntegrationTypes.length > 0) {
        filteredIntegrations = filteredIntegrations.filter((integration) =>
          selectedIntegrationTypes.includes(
            IntegrationsMapping[integration].category
          )
        );
      }
    }

    if (appliedFilters.searchString) {
      filteredIntegrations = filteredIntegrations.filter((integration) => {
        return (
          IntegrationsMapping[integration]?.displayName
            .toLowerCase()
            .includes(appliedFilters.searchString.toLowerCase()) ||
          (IntegrationsMapping[integration]?.codeName &&
            IntegrationsMapping[integration]?.codeName
              .toLowerCase()
              .includes(appliedFilters.searchString.toLowerCase()))
        );
      });
    }

    setFilteredIntegrationsCards(filteredIntegrations);
  }, [appliedFilters]);

  useEffect(() => {
    if (!isIntegratedLoading && integratedList) {
      let integrated_tmp = {};
      integratedList.forEach((integration) => {
        integrated_tmp[integration] = true;
      });

      setIntegrated(integrated_tmp);
    }
  }, [integratedList, isIntegratedLoading]);

  const { data: actionToRoleMapping, isSuccess: isActionToRoleMappingSuccess } =
    useQuery({
      queryKey: userAccessControlKeys.all,
      queryFn: getActionToRoleMapping,
      select: (response) => response.data,
    });

  const { data: platformUser, isSuccess: isPlatformUserSuccess } = useQuery({
    queryKey: platformUsersKeys.user(),
    queryFn: getPlatformUser,
    select: (response) => response.data,
  });

  useEffect(() => {
    if (isActionToRoleMappingSuccess && isPlatformUserSuccess) {
      const permissions = {};

      Object.keys(IntegrationsMapping).forEach((integration) => {
        const authorizedRoles =
          actionToRoleMapping[PLATFORM_USER_ACTIONS.MODIFY_INTEGRATION] || [];
        const userRole = platformUser.access_role;
        const ownUserIntegration = platformUser.ownerships;

        if (integration && ownUserIntegration.includes(integration)) {
          permissions[integration] = true;
        } else {
          permissions[integration] = authorizedRoles.includes(userRole);
        }
      });

      setIntegrationPermissions(permissions);
    }
  }, [
    actionToRoleMapping,
    platformUser,
    isActionToRoleMappingSuccess,
    isPlatformUserSuccess,
  ]);

  const handleChange = (event, newActiveTab) => {
    setActiveTab(newActiveTab);
    setAppliedFilters((prev) => ({ ...prev, integrationStatus: newActiveTab }));
  };

  useEffect(() => {
    const filterFromSessionStorage =
      sessionStorage.getItem("integrationFilter");

    if (filterFromSessionStorage) {
      setAppliedFilters((prev) => ({
        ...prev,
        integrationTypes: {
          [filterFromSessionStorage]: true,
        },
      }));
      sessionStorage.removeItem("integrationFilter");
    } else {
      if (toolFromLink) {
        setAppliedFilters((prev) => ({
          ...prev,
          integrationTypes: {
            [toolFromLink]: true,
          },
        }));
      }
    }

    if (toolNameFromLink) {
      setAppliedFilters((prev) => ({
        ...prev,
        searchString: toolNameFromLink,
      }));
    }
  }, [toolFromLink, toolNameFromLink]);

  const integrationTypeFromQuery = searchParams.get("integrationType");
  if (integrationTypeFromQuery) {
    setAppliedFilters((prev) => ({
      ...prev,
      integrationTypes: {
        [integrationTypeFromQuery]: true,
      },
    }));
    searchParams.delete("integrationType");
    setSearchParams(searchParams);
  }

  const renderIntegrationCard = (
    integration,
    details,
    integrationsInfoQuery
  ) => (
    <Grid item sx={{ minWidth: 330 }}>
      <IntegrationCard
        route={integration}
        image={details?.newLogo ? details?.newLogo : logos?.logo}
        name={details["displayName"]}
        codeName={details["codeName"]}
        description={details["description"]}
        status={integrated[integration]}
        available={details["available"]}
        loading={isIntegratedLoading}
        configurable={details["configurable"]}
        platformUserPermission={integrationPermissions[integration]}
        integrationsInfoQuery={integrationsInfoQuery}
        integrationKey={integration}
      />
    </Grid>
  );

  const integrationsInfoQuery = useQuery({
    queryKey: integrationsInfoKeys.all,
    queryFn: getIntegrationsInfo,
    select: (response) => response.data,
  });

  return (
    <AppPage title="Integrations">
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: colors.common_white,
            boxShadow: "-3px 16px 0 0 " + colors.common_white,
          }}
        >
          <InitialScanBanner />
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "16px",
            }}
            container
          >
            <Grid item xs={2.3}>
              <SearchInput
                fieldName={"searchString"}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
              />
            </Grid>
            <Grid item>
              {" "}
              <FilterMenu
                filterName={"Integration Type"}
                fieldName={"integrationTypes"}
                options={filterOptions.integrationTypes.map((type) => {
                  if (type === "All") {
                    return {
                      name: type,
                      label: "All categories",
                      displayName: type,
                    };
                  }
                  return {
                    name: type, // add entity type to the name to avoid unexpected behavior of components rendering
                    label: type,
                    displayName: type,
                  };
                })}
                appliedFilters={appliedFilters}
                setAppliedFilters={setAppliedFilters}
                fullOptions={filterOptions.integrationTypes}
              />
            </Grid>
          </Grid>

          <TabContext value={activeTab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={handleChange}
                sx={{
                  "& .MuiTabs-indicator": {
                    backgroundColor: colors.primary_main,
                  },
                }}
              >
                {Object.keys(IntegrationTabs).map((title, index) => {
                  return (
                    <Tab
                      value={title}
                      style={{ fontWeight: "500" }}
                      label={
                        title + " (" + groupedIntegrations[title].length + ")"
                      }
                      sx={{
                        textTransform: "none",
                      }}
                    />
                  );
                })}
              </TabList>
            </Box>
          </TabContext>
        </Box>
        <Grid container gap={3} sx={{ mt: 3, paddingBottom: "20px" }}>
          {groupedIntegrations[activeTab].map((integration) =>
            renderIntegrationCard(
              integration,
              IntegrationsMapping[integration],
              integrationsInfoQuery
            )
          )}
        </Grid>
      </Box>
    </AppPage>
  );
};

export default Integrations;
